import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Quote, ArrowRight } from "lucide-react";
import { Layout } from "../components/Layout";
import { useDocumentMeta } from "../lib/useDocumentMeta";
import { fetchSupporters } from "../lib/api";

const fmtMoney = (n) =>
  n == null ? null : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "";
  }
};

export const SupportersPage = () => {
  const [supporters, setSupporters] = useState(null);
  const [error, setError] = useState("");

  useDocumentMeta({
    title: "Our Supporters · Will Not Be Forgotten",
    description: "The people whose generosity keeps this independent civic-memory archive alive. With gratitude.",
    canonical: "https://willnotbeforgotten.org/supporters",
  });

  useEffect(() => {
    let alive = true;
    fetchSupporters()
      .then((d) => alive && setSupporters(d))
      .catch(() => alive && setError("We couldn’t load the supporter wall right now. Please try again shortly."));
    return () => {
      alive = false;
    };
  }, []);

  const count = supporters?.length || 0;

  return (
    <Layout headerVariant="solid">
      <section data-testid="supporters-hero" className="section section--no-border" style={{ paddingTop: "5rem", paddingBottom: "2rem" }}>
        <div className="shell">
          <span className="section__eyebrow">With gratitude</span>
          <h1 style={{ fontSize: "clamp(2.4rem, 4.6vw, 4.25rem)", lineHeight: 1, marginBottom: "1.25rem" }}>
            Our <em style={{ fontStyle: "italic", color: "hsl(var(--accent))" }}>supporters</em>.
          </h1>
          <p className="section__lede" style={{ marginTop: 0, maxWidth: 740 }}>
            This archive stays online because of the people below. Some asked to remain anonymous; all of them
            chose to invest in a fuller, more honest public memory. Thank you.
          </p>
          <div style={{ marginTop: "1.75rem" }}>
            <Link to="/support" className="btn btn--primary" data-testid="supporters-donate-cta">
              <Heart size={15} aria-hidden /> Add your name
            </Link>
          </div>
        </div>
      </section>

      <section className="section" style={{ paddingTop: "1rem" }}>
        <div className="shell">
          {error && (
            <p role="alert" data-testid="supporters-error" style={{ color: "hsl(var(--destructive))" }}>{error}</p>
          )}

          {supporters === null && !error && (
            <p style={{ color: "hsl(var(--muted))" }} data-testid="supporters-loading">Loading supporters…</p>
          )}

          {supporters !== null && count === 0 && !error && (
            <div data-testid="supporters-empty" style={{
              border: "1px dashed hsl(var(--line-strong))", padding: "clamp(2rem, 5vw, 3.5rem)",
              textAlign: "center", background: "hsl(var(--bg-elev))",
            }}>
              <Heart size={28} aria-hidden style={{ color: "hsl(var(--accent))", marginBottom: 12 }} />
              <h2 style={{ marginTop: 0, fontSize: "1.5rem" }}>Be the first to support this archive.</h2>
              <p style={{ maxWidth: 460, margin: "0.75rem auto 1.5rem" }}>
                Every entry here exists because someone refused to let it be forgotten. Help keep that work going.
              </p>
              <Link to="/support" className="btn btn--accent" data-testid="supporters-empty-cta">
                <Heart size={15} aria-hidden /> Become a supporter
              </Link>
            </div>
          )}

          {count > 0 && (
            <>
              <p style={{ fontSize: "0.85rem", color: "hsl(var(--muted))", marginBottom: "1.5rem" }} data-testid="supporters-count">
                {count} {count === 1 ? "supporter" : "supporters"} and counting.
              </p>
              <div data-testid="supporters-grid" style={{
                display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "1rem",
              }}>
                {supporters.map((s) => (
                  <article key={s.id} data-testid="supporter-card" style={{
                    border: "1px solid hsl(var(--line))", background: "hsl(var(--bg-elev))",
                    padding: "1.25rem 1.35rem", display: "flex", flexDirection: "column", gap: 10,
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                      <h3 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: "1.15rem", fontWeight: 500 }}>
                        {s.display_name}
                      </h3>
                      <span className="tag" style={{
                        fontSize: "0.62rem", borderColor: "hsl(var(--accent) / 0.4)",
                        color: "hsl(var(--accent))", background: "hsl(var(--accent) / 0.08)", whiteSpace: "nowrap",
                      }}>
                        {s.frequency_label}
                      </span>
                    </div>

                    {s.message && (
                      <p style={{
                        margin: 0, position: "relative", paddingLeft: 22,
                        fontFamily: "var(--font-display)", fontStyle: "italic",
                        fontSize: "0.96rem", lineHeight: 1.5, color: "hsl(var(--ink-soft))",
                      }}>
                        <Quote size={13} aria-hidden style={{ position: "absolute", left: 0, top: 4, color: "hsl(var(--accent))" }} />
                        {s.message}
                      </p>
                    )}

                    <div style={{
                      marginTop: "auto", display: "flex", justifyContent: "space-between", alignItems: "baseline",
                      gap: 8, fontSize: "0.78rem", color: "hsl(var(--muted))",
                      borderTop: "1px solid hsl(var(--line))", paddingTop: 10,
                    }}>
                      <span>{fmtDate(s.date)}</span>
                      {s.amount != null && (
                        <span style={{ fontFamily: "var(--font-display)", fontWeight: 500, color: "hsl(var(--accent))", fontSize: "0.95rem" }}>
                          {fmtMoney(s.amount)}
                        </span>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <section className="section" style={{ background: "hsl(var(--bg-deep))", textAlign: "center" }}>
        <div className="shell" style={{ maxWidth: 640 }}>
          <h2 style={{ marginTop: 0 }}>Join them.</h2>
          <p className="section__lede" style={{ margin: "1rem auto 1.75rem" }}>
            A one-time gift or a recurring pledge — either way, you’re helping make sure these dates are never erased.
          </p>
          <Link to="/support" className="btn btn--primary" data-testid="supporters-footer-cta">
            Support this project <ArrowRight size={15} aria-hidden />
          </Link>
        </div>
      </section>
    </Layout>
  );
};
